import * as THREE from 'three/webgpu';

import { barrierMaterial } from '../render/barrier.js';
import { RENDER_ORDER } from '../render/materials.js';

/**
 * @file `createBoundary` — the map edge, emitted as ONE thing: the four AABB
 * colliders that stop the player AND the glowing barrier that explains why.
 *
 * The framework's rule for this feature is that a wall is never placed without
 * something explaining it, so the collider ring and the visual ring come out of
 * a single call and can never drift apart.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO RINGS, NOT ONE
 * ─────────────────────────────────────────────────────────────────────────────
 *   bounds ──margin──► collider line ──visualOffset──► barrier quads
 *
 * The barrier sits OUTSIDE the colliders. Pushing against the wall therefore
 * parks the player ~1 m short of the glowing sheet instead of inside it, which
 * matters because the material is additive and depth-tested: a camera inside the
 * quad sees a full-screen cyan wash.
 *
 * The collider ring is byte-for-byte the one `BaseLevel.addWorldBounds` builds
 * (north/south run the full width including the corners so the side walls can
 * stop at the corners and nothing leaks through a seam) — deliberately
 * duplicated rather than called, because `createBoundary` takes a `Bounds2` and
 * not a level, and `tests/boundary.test.mjs` pins the two against each other.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HEIGHTFIELD LEVELS
 * ─────────────────────────────────────────────────────────────────────────────
 * Two of the three shipped levels are heightfields, so a barrier pinned to
 * `y = 0` would float over a dune and bury itself in a ridge. Pass `heightAt`
 * and raise `segments`: the base of each column is sampled from the terrain, and
 * `sink` drops the bottom edge below it so the straight chord between two
 * samples can never open a hairline gap over a crest. The buried skirt is free —
 * it is behind the ground in the depth buffer.
 *
 * The barrier is a fixed ~3 m tall and see-through, which is the whole point:
 * at the 52° gameplay pitch anything opaque hides `h × 0.78 m` of the ground
 * behind it. See `render/barrier.js` for that argument in full.
 *
 * @module engine/world/Boundary
 */

/** @typedef {import('../types.js').Bounds2} Bounds2 */
/** @typedef {import('../types.js').Collider} Collider */
/** @typedef {import('../types.js').ColliderTag} ColliderTag */

/**
 * @typedef {Object} Boundary
 * @property {Collider[]} colliders The four-AABB ring. Push these into
 *   `level.colliders`; nothing is pushed for you.
 * @property {THREE.Group} object One group, four wall meshes. Parent it under
 *   `level.root`.
 * @property {THREE.MeshBasicNodeMaterial} material Shared by all four walls.
 * @property {THREE.Mesh[]} meshes North, east, south, west — in that order.
 * @property {() => void} dispose Idempotent: geometries + material.
 */

/**
 * Builds the collider ring and the barrier that explains it.
 *
 * ```js
 * const boundary = createBoundary({ bounds, margin: 0.5, heightAt: field.heightAt, segments: 24 });
 * colliders.push(...boundary.colliders);
 * level.root.add(boundary.object);
 * level.own(boundary);
 * ```
 *
 * Fully deterministic — no RNG anywhere in this module, so two calls with the
 * same options produce identical vertex buffers.
 *
 * @param {Object} o
 * @param {Bounds2} o.bounds Playable extent.
 * @param {number} [o.margin=0.5] Metres of slack outside `bounds` before the
 *   collider line. Matches what every shipped level already used.
 * @param {number} [o.visualOffset=1] Metres further out again for the barrier
 *   quads, so the player never clips the visual.
 * @param {number} [o.height=3.2] Barrier height above the ground line.
 * @param {number} [o.sink=0.35] Metres the bottom edge is pushed below the
 *   ground line. Only meaningful with `heightAt`.
 * @param {number} [o.segments=1] Horizontal subdivisions PER WALL. The effect is
 *   per-fragment, so this exists purely to let the base follow terrain — leave
 *   it at 1 on a flat level, use ~1 per 10 m on a heightfield.
 * @param {((x: number, z: number) => number)|null} [o.heightAt] Ground sampler
 *   for the base of each column. Omit for a flat level.
 * @param {number} [o.thickness=4] Collider box depth, metres. Only has to exceed
 *   the fastest per-step displacement so nothing tunnels through.
 * @param {number} [o.colliderHeight=40] Collider `h`. Stored, not used by the 2D
 *   solver — it is documentation for anything that later reads the ring.
 * @param {number} [o.y=0] Collider floor.
 * @param {ColliderTag} [o.tag='boundary']
 * @param {import('../render/barrier.js').ColorLike} [o.color] → `barrierMaterial`.
 * @param {number} [o.intensity] → `barrierMaterial`.
 * @param {number} [o.nightScale] → `barrierMaterial`.
 * @param {number} [o.fadeNear] → `barrierMaterial`.
 * @param {number} [o.fadeFar] → `barrierMaterial`.
 * @param {number} [o.peak] → `barrierMaterial`.
 * @param {number} [o.ambient] → `barrierMaterial`.
 * @param {number} [o.ambientFar] → `barrierMaterial`.
 * @param {number} [o.patternAmount] → `barrierMaterial`.
 * @param {string} [o.name='boundary']
 * @returns {Boundary}
 */
export function createBoundary(o = {}) {
  const {
    bounds,
    margin = 0.5,
    visualOffset = 1,
    height = 3.2,
    sink = 0.35,
    segments = 1,
    heightAt = null,
    thickness = 4,
    colliderHeight = 40,
    y = 0,
    tag = 'boundary',
    name = 'boundary',
    ...matOpts
  } = o;

  if (!bounds) throw new Error('createBoundary: bounds is required');

  const colliders = boundaryColliders(bounds, margin, {
    thickness,
    height: colliderHeight,
    tag,
    y,
  });

  // ── the visual ring ────────────────────────────────────────────────────────
  const out = margin + visualOffset;
  const x0 = bounds.minX - out;
  const x1 = bounds.maxX + out;
  const z0 = bounds.minZ - out;
  const z1 = bounds.maxZ + out;

  const material = barrierMaterial({ name: `${name}.barrier`, ...matOpts });

  const object = new THREE.Group();
  object.name = name;
  object.renderOrder = RENDER_ORDER.emissive;

  /** A closed rectangle: each wall runs corner to corner, so the four share
   *  edges and never overlap in plan. */
  const runs = [
    ['north', x0, z0, x1, z0],
    ['east', x1, z0, x1, z1],
    ['south', x1, z1, x0, z1],
    ['west', x0, z1, x0, z0],
  ];

  /** @type {THREE.Mesh[]} */
  const meshes = [];
  for (const [side, ax, az, bx, bz] of runs) {
    const geo = wallGeometry(
      /** @type {number} */ (ax),
      /** @type {number} */ (az),
      /** @type {number} */ (bx),
      /** @type {number} */ (bz),
      { height, sink, segments, heightAt, y },
    );
    const mesh = new THREE.Mesh(geo, material);
    mesh.name = `${name}.${side}`;
    mesh.renderOrder = RENDER_ORDER.emissive;
    // Long thin quads bounding-sphere terribly: a 240 m wall's sphere is 120 m
    // across, so it is either always drawn or wrongly rejected. Four draws is
    // cheaper than being wrong.
    mesh.frustumCulled = false;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    object.add(mesh);
    meshes.push(mesh);
  }

  let disposed = false;
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    for (const mesh of meshes) mesh.geometry?.dispose?.();
    material.dispose?.();
    object.removeFromParent();
    object.clear();
  };

  return { colliders, object, material, meshes, dispose };
}

/**
 * The four-AABB ring, extracted so `createBoundary` (which never sees a level)
 * and `BaseLevel.addWorldBounds` (which never sees a `Bounds2` argument) can be
 * proved identical by the test suite.
 *
 * Walls are placed OUTSIDE `bounds`: they START at `bounds ± margin` and extend
 * a further `thickness` outward, so `margin` is the slack the player gets past
 * the last polygon — not how far the wall intrudes.
 *
 * @param {Bounds2} b
 * @param {number} [margin=0]
 * @param {{thickness?: number, height?: number, tag?: ColliderTag, y?: number}} [opts]
 * @returns {Collider[]} north, south, west, east.
 */
export function boundaryColliders(b, margin = 0, opts = {}) {
  const { thickness = 4, height = 40, tag = 'boundary', y = 0 } = opts;
  const t = thickness;
  const x0 = b.minX - margin;
  const x1 = b.maxX + margin;
  const z0 = b.minZ - margin;
  const z1 = b.maxZ + margin;
  return [
    { kind: 'aabb', minX: x0 - t, minZ: z0 - t, maxX: x1 + t, maxZ: z0, y, h: height, tag },
    { kind: 'aabb', minX: x0 - t, minZ: z1, maxX: x1 + t, maxZ: z1 + t, y, h: height, tag },
    { kind: 'aabb', minX: x0 - t, minZ: z0, maxX: x0, maxZ: z1, y, h: height, tag },
    { kind: 'aabb', minX: x1, minZ: z0, maxX: x1 + t, maxZ: z1, y, h: height, tag },
  ];
}

/**
 * One wall: a vertical strip from `(ax,az)` to `(bx,bz)`, non-indexed, two
 * triangles per segment.
 *
 * UV convention (the material depends on it):
 *   `u` = METRES along the wall — not normalised, so the animated pulse has the
 *         same physical wavelength on a 60 m wall and a 240 m one.
 *   `v` = 0 at the ground line, 1 at the top edge. The sunk skirt is negative;
 *         `barrierMaterial` saturates, so it just reads as "underground".
 *
 * No normals: `MeshBasicNodeMaterial` never reads them, and this project's
 * shading is entirely baked vertex colour anyway (this geometry has no bake at
 * all — it is an emissive overlay, not lit surface).
 *
 * @param {number} ax @param {number} az @param {number} bx @param {number} bz
 * @param {{height: number, sink: number, segments: number, heightAt: ((x:number,z:number)=>number)|null, y: number}} o
 * @returns {THREE.BufferGeometry}
 */
function wallGeometry(ax, az, bx, bz, o) {
  const { height, sink, heightAt, y } = o;
  const n = Math.max(1, Math.floor(o.segments) || 1);
  const runLen = Math.hypot(bx - ax, bz - az);

  const pos = new Float32Array(n * 6 * 3);
  const uvs = new Float32Array(n * 6 * 2);

  // v of the bottom edge: `sink` metres below the ground line, expressed in the
  // material's 0..1 height space.
  const vBottom = -sink / height;

  let p = 0;
  let q = 0;
  /**
   * @param {number} t 0..1 along the run
   * @param {boolean} top
   */
  const push = (t, top) => {
    const x = ax + (bx - ax) * t;
    const z = az + (bz - az) * t;
    const base = heightAt ? heightAt(x, z) : y;
    pos[p++] = x;
    pos[p++] = top ? base + height : base - sink;
    pos[p++] = z;
    uvs[q++] = t * runLen;
    uvs[q++] = top ? 1 : vBottom;
  };

  for (let i = 0; i < n; i++) {
    const t0 = i / n;
    const t1 = (i + 1) / n;
    // Two triangles, consistent winding. `DoubleSide` means the winding is only
    // ever a debugging convenience, but a mixed one is a nightmare to read in a
    // wireframe view.
    push(t0, false);
    push(t1, false);
    push(t1, true);
    push(t0, false);
    push(t1, true);
    push(t0, true);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  // Rule 3: non-indexed, always. Nothing merges this, but the whole project
  // reads `geo.index === null` as "safe to bake / safe to merge".
  geo.computeBoundingSphere();
  return geo;
}
